import { PaymentServiceType } from '@dni-types/data-models/ecommerce-payment'
import * as ECPayModule from '@dni-types/payment/ecpay'
import { PAYMENT_ERROR_CODE, PaymentError } from './PaymentError'
import { type PaymentModule, type PaymentSetting } from './types'
import { isECPayLoaded, loadECPayScript, loadJqueryScript } from './utilities'

const ECPayEnvironmentMap = {
  production: 'Prod',
  sandbox: 'Stage',
} as const

export class ECPayCreditCardModule implements PaymentModule<PaymentSetting> {
  isLoaded = false
  isInitialized = false
  appEnv: PaymentSetting['appEnv'] = 'sandbox'
  appKey: Required<PaymentSetting>['appKey'] = ''

  private static instance: ECPayCreditCardModule | null = null

  // Static getInstance method for singleton pattern
  public static getInstance(): ECPayCreditCardModule {
    if (!ECPayCreditCardModule.instance) {
      ECPayCreditCardModule.instance = new ECPayCreditCardModule()
    }
    return ECPayCreditCardModule.instance
  }

  public loadScript = () => Promise.all([loadJqueryScript(), loadECPayScript()])
    .then(() => {
      this.isLoaded = true
    })

  private readonly createPayment = () => {
    if (!isECPayLoaded(window.ECPay)) return
    window.ECPay.createPayment(this.appKey, ECPayModule.Language.zhTW, (errMsg) => {
      if (errMsg) throw new Error(errMsg)
    }, 'V2')
  }

  public configurePayment(details: Omit<PaymentSetting, 'appId'>) {
    console.debug('[ECPay Credit Card] configurePayment details:', details)
    if (!isECPayLoaded(window.ECPay)) return
    if (!details.appKey) {
      throw new PaymentError('[ECPay Credit Card] appKey is required', PaymentServiceType.ECPAY, PAYMENT_ERROR_CODE.INVALID_APP_ID_OR_APP_KEY)
    }
    this.appEnv = details.appEnv
    this.appKey = details.appKey

    const ecPayEnv = ECPayEnvironmentMap[details.appEnv]

    try {
      if (!this.isInitialized) {
        window.ECPay.initialize(ecPayEnv, 1, (errMsg) => {
          if (errMsg) throw new Error(errMsg)
          this.isInitialized = true
          this.createPayment()
        })
      }
      else {
        this.createPayment()
      }
    }
    catch (err) {
      throw new Error(`[ECPay Credit Card] Configuration failed: ${err}`)
    }
  }

  public getPrime(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!isECPayLoaded(window.ECPay)) {
        reject(new Error('[ECPay Credit Card] no ECPay'))
        return
      }
      window.ECPay.getPayToken((paymentInfo, errMsg) => {
        if (paymentInfo.PayToken) {
          resolve(paymentInfo.PayToken)
        }
        else if (errMsg) {
          reject(new Error(errMsg))
        }
        else {
          reject(new Error('[ECPay Credit Card] no PayToken'))
        }
      })
    })
  }
}
